import {Fetch} from "helpers/fetchWrapper";

const analyticsService = {
    _url: `${process.env.REACT_APP_ANALYTICS_URL || "http://localhost:4009/analytics/v1"}`,

    async getDashboardSummary({restaurantId, storeId, period, startDate, endDate} = {}) {
        const queryParams = new URLSearchParams();
        if (restaurantId) queryParams.append("restaurantId", restaurantId);
        if (storeId) queryParams.append("storeId", storeId);
        if (period) queryParams.append("period", period);
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);

        const queryString = queryParams.toString();
        const url = `${this._url}/dashboard/summary${queryString ? `?${queryString}` : ""}`;

        let res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Failed to fetch dashboard summary");
    },

    async getOrdersSummary({restaurantId, storeId, period, startDate, endDate} = {}) {
        const queryParams = new URLSearchParams();
        if (restaurantId) queryParams.append("restaurantId", restaurantId);
        if (storeId) queryParams.append("storeId", storeId);
        if (period) queryParams.append("period", period);
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);

        const queryString = queryParams.toString();
        const url = `${this._url}/dashboard/orders/summary${queryString ? `?${queryString}` : ""}`;

        let res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Failed to fetch orders summary");
    },


    async getRevenue({restaurantId, storeId, period, startDate, endDate, year} = {}) {
        const queryParams = new URLSearchParams();
        if (restaurantId) queryParams.append("restaurantId", restaurantId);
        if (storeId) queryParams.append("storeId", storeId);
        if (period) queryParams.append("period", period);
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);
        if (year) queryParams.append("year", year);

        const queryString = queryParams.toString();
        const url = `${this._url}/dashboard/revenue${queryString ? `?${queryString}` : ""}`;

        let res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Failed to fetch revenue data");
    },

    /**
     * Get paginated list of recent orders
     */
    async getRecentOrders({restaurantId, storeId, period, startDate, endDate, limit, page, sortBy} = {}) {
        const queryParams = new URLSearchParams();
        if (restaurantId) queryParams.append("restaurantId", restaurantId);
        if (storeId) queryParams.append("storeId", storeId);
        if (period) queryParams.append("period", period);
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);
        if (limit) queryParams.append("limit", limit);
        if (page) queryParams.append("page", page);
        if (sortBy) queryParams.append("sortBy", sortBy);

        const queryString = queryParams.toString();
        const url = `${this._url}/dashboard/orders/recent${queryString ? `?${queryString}` : ""}`;

        let res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Failed to fetch recent orders");
    },

    /**
     Get top trending menu items based on order frequency
     */
    async getTrendingMenus({restaurantId, storeId, period, startDate, endDate, limit} = {}) {
        const queryParams = new URLSearchParams();
        if (restaurantId) queryParams.append("restaurantId", restaurantId);
        if (storeId) queryParams.append("storeId", storeId);
        if (period) queryParams.append("period", period);
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);
        if (limit) queryParams.append("limit", limit);

        const queryString = queryParams.toString();
        const url = `${this._url}/dashboard/menus/trending${queryString ? `?${queryString}` : ""}`;

        let res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Failed to fetch trending menus");
    },

    // ─── Reports Page ────────────────────────────────────────────────────────

    /**
     * Paginated order list for the Daily Report tab.
     * Supports: restaurantId, storeId, page, limit, search, startDate, endDate, status, sortBy
     */
    async getDailyReport({restaurantId, storeId, page, limit, search, startDate, endDate, status, sortBy} = {}) {
        const queryParams = new URLSearchParams();
        if (restaurantId) queryParams.append("restaurantId", restaurantId);
        if (storeId) queryParams.append("storeId", storeId);
        if (page) queryParams.append("page", page);
        if (limit) queryParams.append("limit", limit);
        if (search) queryParams.append("search", search);
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);
        if (status) queryParams.append("status", status);
        if (sortBy) queryParams.append("sortBy", sortBy);

        const queryString = queryParams.toString();
        const url = `${this._url}/reports/daily${queryString ? `?${queryString}` : ""}`;

        let res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Failed to fetch daily report");
    },

    /**
     * Weekly report data: daily revenue trend + order distribution + top customers.
     * Supports: restaurantId, storeId, startDate, endDate
     */
    async getWeeklyReport({restaurantId, storeId, startDate, endDate} = {}) {
        const queryParams = new URLSearchParams();
        if (restaurantId) queryParams.append("restaurantId", restaurantId);
        if (storeId) queryParams.append("storeId", storeId);
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);

        const queryString = queryParams.toString();
        const url = `${this._url}/reports/weekly${queryString ? `?${queryString}` : ""}`;

        let res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Failed to fetch weekly report");
    },

    /**
     * Item & category breakdown for Store Summary tab.
     * Supports: restaurantId, storeId, sortBy (quantity|revenue), page, limit, startDate, endDate
     */
    async getStoreSummary({restaurantId, storeId, sortBy, page, limit, startDate, endDate} = {}) {
        const queryParams = new URLSearchParams();
        if (restaurantId) queryParams.append("restaurantId", restaurantId);
        if (storeId) queryParams.append("storeId", storeId);
        if (sortBy) queryParams.append("sortBy", sortBy);
        if (page) queryParams.append("page", page);
        if (limit) queryParams.append("limit", limit);
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);

        const queryString = queryParams.toString();
        const url = `${this._url}/reports/store-summary${queryString ? `?${queryString}` : ""}`;

        let res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Failed to fetch store summary");
    },

    /**
     * Full order detail for the detail drawer/modal.
     * @param {string} orderId - MongoDB ObjectId of the order
     */
    async getOrderDetail(orderId) {
        const url = `${this._url}/reports/order-detail/${orderId}`;

        let res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Failed to fetch order detail");
    },

    /**
     * Trigger a CSV export download for orders.
     * Opens the URL directly so the browser handles the file download.
     */
    exportReport({restaurantId, storeId, startDate, endDate, status, period} = {}) {
        const queryParams = new URLSearchParams();
        if (restaurantId) queryParams.append("restaurantId", restaurantId);
        if (storeId) queryParams.append("storeId", storeId);
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);
        if (status) queryParams.append("status", status);
        if (period) queryParams.append("period", period);

        const queryString = queryParams.toString();
        const url = `${this._url}/reports/export${queryString ? `?${queryString}` : ""}`;
        // Trigger download by navigating the browser to the CSV endpoint
        window.open(url, "_blank");
    },
};

export default analyticsService;
